import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Row, Col, Button } from 'reactstrap';
import Select from 'react-select';
import moment from 'moment';
import Sidebar from '../../Sidebar';
import { useNavigate, useParams } from 'react-router';
import { useCookies } from 'react-cookie';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import ErrorToast from '../../ErrorToast';
import { displayError } from '../../helpers';
import '../../button.css';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllProducts } from '../../services/ProductServices';
import { getAllCustomerMain } from '../../services/CustomerMainServices';
import { getAllCustomerSites } from '../../services/CustomerSiteServices';
import TableRowsConsumption from './TableRowsConsumption';
import {
    cancelConsumption,
    deleteConsumption,
    getConsumptionDetails,
    getProductionOptions,
    updateConsumption,
} from '../../services/ConsumptionNoteServices';

function displayDateToInput(value) {
    if (!value) return '';
    const parts = String(value).split('-');
    if (parts.length === 3 && parts[2].length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return value;
}

function ConsumptionNoteEdit() {
    const { id } = useParams();
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [plants, setPlants] = useState([]);
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [sites, setSites] = useState([]);
    const [dcOptions, setDcOptions] = useState([]);
    const [rowsData, setRowsData] = useState([]);
    const [inProgress, setInProgress] = useState(true);
    const [isActive, setIsActive] = useState(true);
    const [inputs, setInputs] = useState({});

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || []).filter((p) => p.status).map((p) => ({
                value: p.id,
                label: p.plant_alias,
            })));
        });
        getAllProducts(cookies).then((response) => {
            setProducts(response.product_list || response.product_data || []);
        });
        getAllCustomerMain(cookies).then((response) => {
            const list = response.customer_list || response.customer_data || [];
            setCustomers(list.map((c) => ({ value: c.id, label: c.entity_name || c.name })));
        });
        getAllCustomerSites(cookies).then((response) => {
            const list = response.consignee_list || response.consignee_data || [];
            setSites(list.map((s) => ({
                value: s.id,
                label: s.project_name || s.name || s.entity_name,
            })));
        });
    }, [cookies]);

    useEffect(() => {
        if (!id) return;
        setInProgress(true);
        getConsumptionDetails(cookies, id)
            .then((record) => {
                const active = record.status === true || record.status === 1 || record.status === 'Active';
                setIsActive(active);
                setInputs({
                    plant_id: record.plant_id,
                    consumption_date: displayDateToInput(record.consumption_date),
                    consumption_time: record.consumption_time?.slice(0, 5) || '',
                    consumption_no: record.sl_no,
                    prefix: record.prefix,
                    consumption_mode: record.consumption_mode,
                    production_mode: record.production_mode,
                    from_date: displayDateToInput(record.from_date),
                    upto_date: displayDateToInput(record.upto_date),
                    customer_id: record.customer_id || '',
                    consignee_id: record.consignee_id || '',
                    del_challan_id: record.del_challan_id || '',
                    total_concrete_qty: record.total_concrete_qty || 0,
                    remarks: record.remarks || '',
                });
                setRowsData(record.detail_list || []);
                if (record.plant_id) {
                    getProductionOptions(cookies, {
                        plant_id: record.plant_id,
                        from_date: record.from_date,
                        upto_date: record.upto_date,
                    }).then((res) => {
                        setDcOptions((res.delivery_challans || []).map((dc) => ({
                            value: dc.id,
                            label: `${dc.dc_no} | ${dc.dc_date} | Batch ${dc.batch_no}`,
                        })));
                    });
                }
            })
            .catch((error) => displayError(error?.response?.data || error, 'Load Failed'))
            .finally(() => setInProgress(false));
    }, [cookies, id]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInputs((prev) => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name, option) => {
        setInputs((prev) => ({ ...prev, [name]: option?.value || '' }));
    };

    const buildPayload = () => ({
        plant_id: inputs.plant_id,
        consumption_date: moment(inputs.consumption_date, 'YYYY-MM-DD').format('DD-MM-YYYY'),
        consumption_time: inputs.consumption_time,
        consumption_no: inputs.consumption_no,
        prefix: inputs.prefix,
        consumption_mode: inputs.consumption_mode,
        production_mode: inputs.production_mode,
        from_date: inputs.from_date ? moment(inputs.from_date, 'YYYY-MM-DD').format('DD-MM-YYYY') : null,
        upto_date: inputs.upto_date ? moment(inputs.upto_date, 'YYYY-MM-DD').format('DD-MM-YYYY') : null,
        customer_id: inputs.customer_id || null,
        consignee_id: inputs.consignee_id || null,
        del_challan_id: inputs.del_challan_id || null,
        total_concrete_qty: parseFloat(inputs.total_concrete_qty) || 0,
        user_remarks: inputs.remarks,
        detail_list: rowsData.map((row, idx) => ({
            line_no: idx + 1,
            product_id: row.product_id,
            material_label: row.material_label,
            design_mix_qty: parseFloat(row.design_mix_qty) || 0,
            consumed_qty: parseFloat(row.consumed_qty) || 0,
            unit: row.unit || 'Kg',
        })),
    });

    const handleUpdate = (event) => {
        event.preventDefault();
        setInProgress(true);
        updateConsumption(cookies, id, buildPayload())
            .then(() => {
                Swal.fire('Updated', 'Consumption note updated.', 'success');
                navigate('/ConsumptionNoteTable');
            })
            .catch((error) => displayError(error?.response?.data || error, 'Update Failed'))
            .finally(() => setInProgress(false));
    };

    const handleCancel = () => {
        Swal.fire({
            title: 'Cancel consumption note?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel',
        }).then((result) => {
            if (!result.isConfirmed) return;
            setInProgress(true);
            cancelConsumption(cookies, id)
                .then((res) => {
                    Swal.fire('Cancelled', res.message || 'Cancelled.', 'success');
                    setIsActive(false);
                })
                .catch((error) => displayError(error?.response?.data || error, 'Cancel Failed'))
                .finally(() => setInProgress(false));
        });
    };

    const handleDelete = () => {
        Swal.fire({
            title: 'Delete cancelled consumption?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
        }).then((result) => {
            if (!result.isConfirmed) return;
            setInProgress(true);
            deleteConsumption(cookies, id)
                .then(() => {
                    Swal.fire('Deleted', 'Consumption note deleted.', 'success');
                    navigate('/ConsumptionNoteTable');
                })
                .catch((error) => displayError(error?.response?.data || error, 'Delete Failed'))
                .finally(() => setInProgress(false));
        });
    };

    const readOnly = !isActive;

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <ErrorToast />
            <LoadingOverlay active={inProgress} />
            <div id="page-wrap">
                <form onSubmit={handleUpdate}>
                    <FloatingControls
                        tableLink="/ConsumptionNoteTable"
                        formLink="/ConsumptionNote"
                        addNewLabel="Main Form"
                        enableCancel={false}
                        showSave={!readOnly}
                    />

                    <FormPageLayout
                        moduleLabel="STORE"
                        title="Consumption"
                        subtitle={readOnly
                            ? 'Read-only view of the material consumption voucher and line details.'
                            : 'Update production criteria, material lines, and remarks for this consumption voucher.'}
                        modeLabel={readOnly ? 'VIEW MODE' : 'UPDATE MODE'}
                    >
                    <div className="container form-control-panel">
                        <Row>
                            <Col xl={6} lg={12} md={12}>
                                <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                    <h4 className="col-sm-11"><b>Production Criteria</b></h4><br /><br />

                                    <label htmlFor="plant_id" className="form-group col-sm-5 text-right">Plant</label>
                                    <select
                                        id="plant_id"
                                        name="plant_id"
                                        className="form-control col-sm-6 browser-default custom-select"
                                        value={inputs.plant_id || ''}
                                        onChange={handleChange}
                                        disabled={readOnly}
                                    >
                                        <option value="">Select Plant</option>
                                        {plants.map((p) => (
                                            <option key={p.value} value={p.value}>{p.label}</option>
                                        ))}
                                    </select><br />

                                    <label htmlFor="consumption_mode" className="form-group col-sm-5 text-right">Consumption Basis</label>
                                    <select
                                        id="consumption_mode"
                                        name="consumption_mode"
                                        className="form-control col-sm-6"
                                        value={inputs.consumption_mode || ''}
                                        onChange={handleChange}
                                        disabled={readOnly}
                                    >
                                        <option value="datewise">Datewise</option>
                                        <option value="batchwise">Batchwise</option>
                                        <option value="customerwise">Customerwise</option>
                                        <option value="sitewise">Sitewise</option>
                                    </select><br />

                                    <label htmlFor="production_mode" className="form-group col-sm-5 text-right">Production Source</label>
                                    <select
                                        id="production_mode"
                                        name="production_mode"
                                        className="form-control col-sm-6"
                                        value={inputs.production_mode || ''}
                                        onChange={handleChange}
                                        disabled={readOnly}
                                    >
                                        <option value="auto">Auto</option>
                                        <option value="manual">Manual</option>
                                    </select><br />

                                    {inputs.consumption_mode !== 'batchwise' && (
                                        <>
                                            <label htmlFor="from_date" className="form-group col-sm-5 text-right">From Date</label>
                                            <input
                                                type="date"
                                                id="from_date"
                                                name="from_date"
                                                className="form-control col-sm-6"
                                                value={inputs.from_date || ''}
                                                onChange={handleChange}
                                                disabled={readOnly}
                                            /><br />

                                            <label htmlFor="upto_date" className="form-group col-sm-5 text-right">Upto Date</label>
                                            <input
                                                type="date"
                                                id="upto_date"
                                                name="upto_date"
                                                className="form-control col-sm-6"
                                                value={inputs.upto_date || ''}
                                                onChange={handleChange}
                                                disabled={readOnly}
                                            /><br />
                                        </>
                                    )}

                                    {inputs.consumption_mode === 'customerwise' && (
                                        <>
                                            <label className="form-group col-sm-5 text-right">Customer</label>
                                            <div className="col-sm-6 p-0 mb-2">
                                                <Select
                                                    isDisabled={readOnly}
                                                    options={customers}
                                                    value={customers.find((c) => c.value === inputs.customer_id) || null}
                                                    onChange={(opt) => handleSelectChange('customer_id', opt)}
                                                />
                                            </div><br />
                                        </>
                                    )}

                                    {inputs.consumption_mode === 'sitewise' && (
                                        <>
                                            <label className="form-group col-sm-5 text-right">Site (Consignee)</label>
                                            <div className="col-sm-6 p-0 mb-2">
                                                <Select
                                                    isDisabled={readOnly}
                                                    options={sites}
                                                    value={sites.find((s) => s.value === inputs.consignee_id) || null}
                                                    onChange={(opt) => handleSelectChange('consignee_id', opt)}
                                                />
                                            </div><br />
                                        </>
                                    )}

                                    {inputs.consumption_mode === 'batchwise' && (
                                        <>
                                            <label className="form-group col-sm-5 text-right">Delivery Challan / Batch</label>
                                            <div className="col-sm-6 p-0 mb-2">
                                                <Select
                                                    isDisabled={readOnly}
                                                    options={dcOptions}
                                                    value={dcOptions.find((d) => d.value === inputs.del_challan_id) || null}
                                                    onChange={(opt) => handleSelectChange('del_challan_id', opt)}
                                                />
                                            </div><br />
                                        </>
                                    )}
                                </div>
                            </Col>

                            <Col xl={6} lg={12} md={12}>
                                <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                    <h4 className="col-sm-11"><b>Consumption Note Details</b></h4><br /><br />

                                    <label className="form-group col-sm-4 text-right">Consumption No</label>
                                    <input
                                        type="text"
                                        className="form-control col-sm-7"
                                        value={`${inputs.prefix || ''}/${inputs.consumption_no || ''}`}
                                        disabled
                                        style={{ cursor: 'not-allowed' }}
                                    /><br />

                                    <label htmlFor="consumption_date" className="form-group col-sm-4 text-right">Date</label>
                                    <input
                                        type="date"
                                        id="consumption_date"
                                        name="consumption_date"
                                        className="form-control col-sm-7"
                                        value={inputs.consumption_date || ''}
                                        onChange={handleChange}
                                        disabled={readOnly}
                                    /><br />

                                    <label htmlFor="consumption_time" className="form-group col-sm-4 text-right">Time</label>
                                    <input
                                        type="time"
                                        id="consumption_time"
                                        name="consumption_time"
                                        className="form-control col-sm-7"
                                        value={inputs.consumption_time || ''}
                                        onChange={handleChange}
                                        disabled={readOnly}
                                    /><br />

                                    <label htmlFor="total_concrete_qty" className="form-group col-sm-4 text-right">Total Concrete (cum)</label>
                                    <input
                                        type="number"
                                        id="total_concrete_qty"
                                        name="total_concrete_qty"
                                        className="form-control col-sm-7"
                                        style={{ textAlign: 'right' }}
                                        value={inputs.total_concrete_qty || 0}
                                        onChange={handleChange}
                                        disabled={readOnly}
                                    /><br />

                                    <label className="form-group col-sm-4 text-right">Status</label>
                                    <input
                                        type="text"
                                        className="form-control col-sm-7"
                                        value={isActive ? 'Active' : 'Cancelled'}
                                        disabled
                                        style={{ cursor: 'not-allowed' }}
                                    /><br />
                                </div>
                            </Col>
                        </Row>

                        <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue container table-responsive">
                            <br />
                            <table className="table order-list" style={{ textAlign: 'center', borderRadius: '10px', backgroundColor: 'RGB(188,232,253)' }}>
                                <thead>
                                    <tr>
                                        {!readOnly && <th>Action</th>}
                                        <th>S.No.</th>
                                        <th>Material</th>
                                        <th>Bin / Silo</th>
                                        <th>Design Mix (kg/cum)</th>
                                        <th>Consumed Qty</th>
                                        <th>Unit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <TableRowsConsumption
                                        rowsData={rowsData}
                                        products={products}
                                        onChange={setRowsData}
                                        onRemove={(index) => setRowsData((rows) => rows.filter((_, i) => i !== index))}
                                        readOnly={readOnly}
                                    />
                                </tbody>
                            </table>
                        </div>

                        <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                            <label htmlFor="remarks" className="form-group col-sm-2 text-right">Remarks</label>
                            <textarea
                                rows="2"
                                cols="50"
                                className="form-control col-sm-10"
                                id="remarks"
                                name="remarks"
                                value={inputs.remarks || ''}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </div>
                    </div>

                    <div className="footer text-center">
                        {!readOnly && (
                            <Button type="submit" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }}>Update</Button>
                        )}
                        {isActive && (
                            <Button type="button" className="btn btn-twitter" style={{ width: '100px', fontWeight: 'bold' }} onClick={handleCancel}>Cancel Voucher</Button>
                        )}
                        {!isActive && (
                            <Button type="button" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }} onClick={handleDelete}>Delete</Button>
                        )}
                        <Button type="button" className="btn btn-twitter" style={{ width: '110px', fontWeight: 'bold' }} onClick={() => navigate('/ConsumptionNote')}>Main Form</Button>
                        <Button type="button" className="btn btn-twitter" style={{ width: '110px', fontWeight: 'bold' }} onClick={() => navigate('/ConsumptionNoteTable')}>View</Button>
                        <Button type="button" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }} onClick={() => navigate('/Home')}>Home</Button>
                    </div>
                    </FormPageLayout>
                </form>
            </div>
        </div>
    );
}

export default ConsumptionNoteEdit;
